import { init_app } from './lib/api-calls.js';
import { trackAsync } from './lib/loading/helpers.js';
import { LoadingType } from './lib/loading/types.js';
import { BootstrapSources } from './lib/loading/sources.js';
import { preferences } from './lib/stores/preferences.svelte.js';


function get_cookie(name) {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
}


export const session = $state({
    loaded: false,
    is_authenticated: false,
    is_superuser: false,
    csrf_token: null,
    user: null,
    // Server-side settings the client needs, from /api/init (e.g. meta_pixel_id).
    config: {},
    // Content language is reactive app state so changing it on a question
    // immediately updates every question surface without a reload.
    content_language: preferences.contentLanguage === 'en' ? 'en' : 'fr',
});


/**
 * Hydrate `session` from the server.
 *
 * Called by the entry point rather than on import, because the Telegram entry
 * has to exchange `initData` for a bearer token first — hydrating before the
 * token exists would read as an anonymous session and bounce to login.
 *
 * @returns {Promise<void>} Resolves once `session.loaded` is true, whatever the
 * outcome; a failed init is a signed-out session, not a crash.
 */
/** @type {Promise<void>|null} */
let session_ready = null;

// NavigationManager is constructed while the module graph is evaluating,
// before the web entry point gets a chance to call bootstrap_session(). Routes
// can therefore ask for sessionReady() a few microtasks before `session_ready`
// exists. Keep those callers parked until bootstrap actually starts rather than
// treating "not started yet" as "already ready". Telegram relies on the same
// distinction because it authenticates its transport before starting bootstrap.
let resolve_session_started;
const session_started = new Promise((resolve) => {
    resolve_session_started = resolve;
});

/**
 * Resolves once the session bootstrap has settled, either way.
 *
 * Routes that serve a different page to signed-in and signed-out visitors
 * cannot choose until this resolves: on a hard load the router reaches the
 * route before `init_app` answers, and reading `is_authenticated` then gives
 * the default — signed out — for every visitor, signed in or not.
 */
export function sessionReady() {
    if (session.loaded) return Promise.resolve();
    if (session_ready) return session_ready;
    return session_started.then(() => session_ready);
}

export function bootstrap_session() {
    if (session_ready) return session_ready;

    session_ready = trackAsync(BootstrapSources.SESSION_INIT, async () => {
        const response = await init_app();
        const data = response.response_data;
        session.is_authenticated = data.user?.is_authenticated || false;
        session.is_superuser = data.user?.is_superuser || false;
        session.user = data.user || null;
        session.config = data.config || {};
        const serverLanguage = data.user?.content_language;
        session.content_language = serverLanguage === 'en' ? 'en' : serverLanguage === 'fr' ? 'fr' : preferences.contentLanguage;
        // The server preference wins for signed-in users. Mirroring it locally
        // keeps the same choice through the brief signed-out/bootstrap window.
        preferences.contentLanguage = session.content_language;
        session.csrf_token = get_cookie('csrftoken');
    }, { type: LoadingType.Bootstrap }).catch((error) => {
        console.error("Session initialization failed:", error);
        session.is_authenticated = false;
        session.is_superuser = false;
    }).finally(() => {
        session.loaded = true;
    });
    resolve_session_started?.();
    resolve_session_started = null;
    return session_ready;
}
